import { inject, Injectable } from '@angular/core';
import {
  ChangeLogEntityType,
  ChangeLogEntry,
  ChangeSet,
} from '../models/change-log.model';
import { AuthService } from './auth.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Field-level audit trail for record edits. Entity services call
 * {@link record} after an UPDATE; the address / lead detail screens render the
 * entries as a timeline alongside interactions.
 *
 * Deliberately independent of `_sync_log`, which is a transport log with its
 * own lifecycle. Offline-first like every other table: rows are written
 * locally and propagate via sync.
 */
@Injectable({
  providedIn: 'root',
})
export class ChangeLogService {
  private db = inject(DatabaseService);
  private clockSync = inject(ClockSyncService);
  private authService = inject(AuthService);
  private logger = inject(LoggerService).getLogger('change-log');

  /**
   * Compute the diff between `before` and `after` over `fields` and, if
   * anything changed, insert a change_log row. Returns the change set written,
   * or `null` when nothing changed (no row is written in that case).
   */
  async record<T extends object>(
    entityType: ChangeLogEntityType,
    entityId: string,
    before: T,
    after: T,
    fields: readonly (keyof T)[],
  ): Promise<ChangeSet | null> {
    const changes = this.diff(before, after, fields);
    if (!changes) return null;

    const userId = this.authService.getCurrentUserId() ?? '';
    await this.db.init();
    const now = this.clockSync.now();

    await this.db.run(
      `INSERT INTO change_log (id, entity_type, entity_id, changed_by, changed_at, changes, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?)`,
      [this.db.generateId(), entityType, entityId, userId, now, JSON.stringify(changes), now],
    );

    this.logger.debug('Recorded change:', { entityType, entityId, fields: Object.keys(changes) });
    return changes;
  }

  /** Pure diff helper — exported for tests and for callers that only need the set. */
  diff<T extends object>(before: T, after: T, fields: readonly (keyof T)[]): ChangeSet | null {
    const changes: ChangeSet = {};
    for (const field of fields) {
      const from = before[field];
      const to = after[field];
      if (from !== to) {
        changes[String(field)] = { from, to };
      }
    }
    return Object.keys(changes).length > 0 ? changes : null;
  }

  /** All entries for one entity, newest first. */
  async getFor(entityType: ChangeLogEntityType, entityId: string): Promise<ChangeLogEntry[]> {
    await this.db.init();
    const rows = await this.db.exec(
      'SELECT * FROM change_log WHERE entity_type = ? AND entity_id = ? ORDER BY changed_at DESC',
      [entityType, entityId],
    );
    return rows as ChangeLogEntry[];
  }

  /** Reactive query for one entity's history. */
  createQueryFor(entityType: ChangeLogEntityType, entityId: string): QuerySignal<ChangeLogEntry[]> {
    return this.db.createQuery('change_log', () => this.getFor(entityType, entityId));
  }

  /** Decode the JSON `changes` column. Tolerates malformed rows by returning `{}`. */
  parseChanges(entry: ChangeLogEntry): ChangeSet {
    try {
      return JSON.parse(entry.changes) as ChangeSet;
    } catch {
      return {};
    }
  }
}
